import CustomDialog from "components/Atoms/CustomDialog"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import ButtonMutation from "components/Molecules/ButtonMutation"
import FileUpload from "components/Molecules/FileUpload"
import FormController from "components/Molecules/FormController"
import dayjs from "dayjs"
import { PAYMENT_METHODS } from "data/static.items"
import { SETTLE_MULTIPLE_ORDERS } from "graphql/order/order.mutations"
import useAuth from "hooks/useAuth"
import { cloneDeep, set, sumBy } from "lodash-es"
import React, { useMemo, useState } from "react"
import { ErrorToast, SuccessToast } from "utils/AppToaster"
import { inr } from "utils/formatters.utils"

export function allocateSettlement(orders: any[], amount: number) {
  let remaining = Number.isFinite(amount) && amount > 0 ? amount : 0
  return [...orders]
    .sort(
      (a, b) => dayjs(a?.createdAt).valueOf() - dayjs(b?.createdAt).valueOf()
    )
    .map(order => {
      const outstanding = Number(order?.outstandingAmount) || 0
      const settled = Math.round(Math.min(outstanding, remaining) * 100) / 100
      remaining = Math.round((remaining - settled) * 100) / 100
      return { order, outstanding, settled, balance: outstanding - settled }
    })
}

const MultipleOrderAddTransaction = ({ isOpen, onClose, orders, onDone }) => {
  const {
    user: { id: userId, currentDepartment },
  } = useAuth() as any
  const [state, setState] = useState<any>({})

  const totalOutstanding = useMemo(
    () => sumBy(orders, (o: any) => Number(o?.outstandingAmount) || 0),
    [orders]
  )
  const totalAmount = sumBy(orders, (o: any) => Number(o?.totalAmount) || 0)
  const totalPaid = sumBy(orders, (o: any) => Number(o?.totalPaid) || 0)
  const businessName =
    orders?.[0]?.shop?.name ||
    orders?.[0]?.user?.shopsManaged?.[0]?.name ||
    "--"
  const amount = Number(state?.amount)
  const allocation = useMemo(
    () => allocateSettlement(orders, amount),
    [orders, amount]
  )
  const totalSettled = sumBy(allocation, a => a.settled)

  function handleChange(path, value) {
    const copy = cloneDeep(state)
    set(copy, path, value)
    setState(copy)
  }

  const variables = {
    orderIds: allocation.filter(a => a.settled > 0).map(a => a.order.id),
    amount: state?.amount ? String(state.amount) : state?.amount,
    paymentMethod: state?.paymentMethod?.id,
    aggregatorId: userId,
    departmentId: currentDepartment?.id,
    attachment: state?.attachment
      ? { url: state.attachment, mediaType: "FLE" }
      : undefined,
  }

  return (
    <CustomDialog
      icon="th-list"
      light
      width="50%"
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span>
          <span className="vt-muted"> Payment Settlement - </span>
          {orders.length} Orders
        </span>
      }
      footerRightButton={
        <ButtonMutation
          onError={() =>
            ErrorToast("Unable to settle the payment, please try again!")
          }
          onCompleted={({ multiOrderPaymentSettle: res }) => {
            SuccessToast(
              `Successfully settled ${inr(amount)} across ${
                res?.orderTransactions?.length ?? 0
              } transactions`
            )
            setState({})
            onDone?.()
            onClose()
          }}
          validate={() => {
            if (!(amount > 0)) {
              return ErrorToast("Please enter a valid amount!")
            }
            if (!state?.paymentMethod?.id) {
              return ErrorToast("Please select a valid payment method!")
            }
            if (amount > totalOutstanding) {
              return ErrorToast(
                `Amount ${inr(amount)} exceeds the total outstanding ${inr(
                  totalOutstanding
                )}`
              )
            }
            return true
          }}
          icon="confirm"
          text="Settle"
          mutation={SETTLE_MULTIPLE_ORDERS}
          variables={variables}
        />
      }
    >
      <FormController source={state} onChange={handleChange}>
        <div className="mb-3 flex flex-wrap items-center text-sm">
          {[
            ["Business Name:", <b key="b">{businessName} </b>],
            ["Order:", <b key="o">{orders.length}</b>],
            ["Total Amount:", <b key="t">{inr(totalAmount)} </b>],
            [
              "Payment:",
              <b key="p" className="vt-success">
                {inr(totalPaid)}
              </b>,
            ],
            [
              "Outstanding",
              <b key="u" className="vt-danger">
                {inr(totalOutstanding)}
              </b>,
            ],
          ].map(([label, value], i) => (
            <div
              key={i}
              className={`flex gap-2 ${i ? "border-l pl-3 ml-3" : ""}`}
            >
              <span>{label}</span>
              {value}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-4">
          <StringInput
            required
            big={false}
            leftIcon="rupee"
            type="number"
            label="Amount"
            placeholder="Enter"
            path="amount"
          />
          <StaticSelector
            absolute
            required
            items={PAYMENT_METHODS}
            path="paymentMethod"
            label="Payment Method"
          />
          <FileUpload label="Photo" foldername="payments" path="attachment" />
        </div>
      </FormController>

      <div className="mt-4 border rounded-lg">
        <div className="px-3 py-2 border-b font-600">
          Settlement Summary{" "}
          <span className="text-xs text-gray-600 font-400">
            (oldest order first)
          </span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-600">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2 text-right">Outstanding</th>
              <th className="px-3 py-2 text-right">Settling</th>
              <th className="px-3 py-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {allocation.map(({ order, outstanding, settled, balance }, i) => (
              <tr key={order.id} className="border-t">
                <td className="px-3 py-2">{i + 1}</td>
                <td className="px-3 py-2">{order.trackingCode}</td>
                <td className="px-3 py-2">
                  {dayjs(order.createdAt).format("DD MMM YYYY")}
                </td>
                <td className="px-3 py-2 text-right">{inr(outstanding)}</td>
                <td className="px-3 py-2 text-right vt-success">
                  {inr(settled)}
                </td>
                <td className="px-3 py-2 text-right vt-danger">
                  {inr(balance)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t font-600">
              <td className="px-3 py-2" colSpan={3}>
                Total
              </td>
              <td className="px-3 py-2 text-right">{inr(totalOutstanding)}</td>
              <td className="px-3 py-2 text-right vt-success">
                {inr(totalSettled)}
              </td>
              <td className="px-3 py-2 text-right vt-danger">
                {inr(totalOutstanding - totalSettled)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </CustomDialog>
  )
}

export default MultipleOrderAddTransaction
